/**
 * CMPメインロジック（初期化・イベント調整）
 *
 * Clipkit.cmpの公開APIを提供し、各モジュールを統合する。
 * DOMContentLoadedで<script type="application/json" id="cmp-config">から設定を読み取り、
 * 自動的に初期化する。
 */

import { validateConfig } from './config';
import { readConsent, writeConsent, deleteConsent, generateUUID, resetMemoryFallback } from './cookie';
import { resolveTexts } from './i18n';
import { sendConsentLog } from './api';
import { injectStyles, createBanner, createPreferencesModal, createRevisitButton } from './ui';
import { processTemplates, startObserver, stopObserver } from './element-manager';

// 内部状態
let _config = null;
let _texts = null;
let _bannerEl = null;
let _modalEl = null;
let _revisitEl = null;
let _focusBeforeModal = null;
let _initialized = false;

/**
 * 初期化
 * @param {Object} rawConfig - パース済みの設定オブジェクト
 */
function run(rawConfig) {
  const { valid, config, errors } = validateConfig(rawConfig);

  if (!valid) {
    errors.forEach((e) => console.error('[CMP]', e));
    // 必須カテゴリのtemplateのみアクティベート
    activateRequiredOnly(rawConfig);
    return;
  }

  _config = config;
  _texts = resolveTexts(config.translations, config.defaultLanguage);

  injectStyles();

  const consent = readConsent(config.cookie.name);

  if (consent && !needsReConsent(consent, config.categories)) {
    // 同意済み — templateを展開してイベント発火
    applyConsent(consent.categories);
    dispatchConsentEvent('initial', consent.categories);
    showRevisitButton();
  } else {
    // 未同意またはカテゴリ変更 — バナー表示
    showBanner();
  }

  // data-cmp-action のdocumentレベルイベント委譲（重複登録防止）
  if (!_initialized) {
    document.addEventListener('click', handleDataAction);
    _initialized = true;
  }
}

/**
 * カテゴリ変更検知（再同意が必要か判定）
 * Cookie内のcategoriesキーと設定のcategoriesキーを比較する
 */
function needsReConsent(consent, categories) {
  const cookieKeys = Object.keys(consent.categories).sort();
  const configKeys = categories.map((c) => c.key).sort();
  // カテゴリの追加・削除を検知
  if (cookieKeys.join(',') !== configKeys.join(',')) return true;
  // required属性の変更を検知（GDPR: 目的変更時は再同意が必要）
  return categories.some((cat) => cat.required && !consent.categories[cat.key]);
}

/**
 * 同意を適用する（template展開 + MutationObserver開始）
 */
function applyConsent(categories) {
  const checker = (category) => Boolean(categories[category]);
  processTemplates(checker);
  startObserver(checker);
}

/**
 * 初期化エラー時 — 必須カテゴリのtemplateのみアクティベート
 */
function activateRequiredOnly(rawConfig) {
  if (!rawConfig || !Array.isArray(rawConfig.categories)) return;
  const requiredKeys = rawConfig.categories.filter((c) => c && c.required).map((c) => c.key);
  const checker = (category) => requiredKeys.includes(category);
  processTemplates(checker);
  startObserver(checker);
}

/**
 * 同意バナーを表示する
 */
function showBanner() {
  if (_bannerEl) return;

  _bannerEl = createBanner(_texts, {
    onAcceptAll: () => acceptAll(),
    onAcceptNecessary: () => acceptNecessary(),
    onShowPreferences: () => showPreferences()
  });

  document.body.appendChild(_bannerEl);

  // バナーコンテナにフォーカス（特定ボタンへの誘導を避けGDPR中立性を確保）
  _bannerEl.focus();
}

/**
 * 同意バナーを非表示にする
 */
function hideBanner() {
  if (_bannerEl) {
    _bannerEl.remove();
    _bannerEl = null;
  }
}

/**
 * リビジットボタンを表示する
 */
function showRevisitButton() {
  if (_revisitEl) return;

  _revisitEl = createRevisitButton(() => showPreferences(), _texts);
  document.body.appendChild(_revisitEl);
}

/**
 * リビジットボタンを非表示にする
 */
function hideRevisitButton() {
  if (_revisitEl) {
    _revisitEl.remove();
    _revisitEl = null;
  }
}

/**
 * 設定モーダルを表示する
 */
function showPreferences() {
  if (!_config) return;
  if (_modalEl) return;

  hideRevisitButton();
  _focusBeforeModal = document.activeElement;

  const consent = readConsent(_config.cookie.name);
  const currentCategories = {};
  _config.categories.forEach((cat) => {
    if (consent && consent.categories) {
      currentCategories[cat.key] = cat.required ? true : Boolean(consent.categories[cat.key]);
    } else {
      currentCategories[cat.key] = cat.required;
    }
  });

  const modalCallbacks = {
    onSave: (selections) => savePreferences(selections),
    onAcceptAll: () => acceptAll(),
    onAcceptNecessary: () => acceptNecessary(),
    onClose: () => closePreferencesModal()
  };
  // 同意済みの場合のみ撤回ボタンを表示
  if (consent) {
    modalCallbacks.onRevoke = () => reset();
  }

  _modalEl = createPreferencesModal(_texts, _config.categories, currentCategories, modalCallbacks);

  document.body.appendChild(_modalEl);

  // モーダル内の最初のフォーカス可能要素にフォーカス
  const firstFocusable = _modalEl.querySelector('button');
  if (firstFocusable) firstFocusable.focus();
}

/**
 * 設定モーダルを閉じる（同意状態は変更しない）
 */
function closePreferencesModal() {
  if (_modalEl) {
    _modalEl.remove();
    _modalEl = null;
  }
  // モーダルを開いた要素にフォーカスを戻す
  if (_focusBeforeModal) {
    _focusBeforeModal.focus();
    _focusBeforeModal = null;
  }
  // バナーが表示されていない場合のみリビジットボタンを再表示
  if (!_bannerEl) {
    const consent = _config ? readConsent(_config.cookie.name) : null;
    if (consent) {
      showRevisitButton();
    }
  }
}

/**
 * 全カテゴリ許可
 */
function acceptAll() {
  if (!_config) return;
  const categories = {};
  _config.categories.forEach((cat) => {
    categories[cat.key] = true;
  });
  commitConsent(categories, determineAction());
}

/**
 * 必須のみ許可
 */
function acceptNecessary() {
  if (!_config) return;
  const categories = {};
  _config.categories.forEach((cat) => {
    categories[cat.key] = cat.required;
  });
  commitConsent(categories, determineAction());
}

/**
 * 個別カテゴリの同意を保存
 * @param {Object} selections - { analytics: true, ... }
 */
function savePreferences(selections) {
  if (!_config) return;
  const categories = {};
  _config.categories.forEach((cat) => {
    categories[cat.key] = cat.required ? true : Boolean(selections[cat.key]);
  });
  commitConsent(categories, determineAction());
}

/**
 * アクションタイプを判定する（initial / update）
 */
function determineAction() {
  const existing = readConsent(_config.cookie.name);
  return existing ? 'update' : 'initial';
}

/**
 * 同意を確定する（Cookie保存・ログ送信・UI更新・template展開・イベント発火）
 */
function commitConsent(categories, action) {
  const existing = readConsent(_config.cookie.name);
  const consent = {
    id: existing && existing.id ? existing.id : generateUUID(),
    categories,
    timestamp: new Date().toISOString()
  };

  writeConsent(_config.cookie.name, consent, _config.cookie);

  sendConsentLog(_config.consentLogUrl, {
    consent_id: consent.id,
    categories: consent.categories,
    action
  });

  hideBanner();
  closePreferencesModal();
  applyConsent(categories);
  dispatchConsentEvent(action, categories);
}

/**
 * 同意をリセットする（Cookie削除 → revokeログ送信 → バナー再表示）
 */
function reset() {
  if (!_config) return;

  const consent = readConsent(_config.cookie.name);
  if (consent) {
    sendConsentLog(_config.consentLogUrl, {
      consent_id: consent.id,
      categories: consent.categories,
      action: 'revoke'
    });
  }

  deleteConsent(_config.cookie.name, _config.cookie.path);
  stopObserver();

  const revokedCategories = {};
  if (_config.categories) {
    _config.categories.forEach((cat) => {
      revokedCategories[cat.key] = cat.required;
    });
  }
  dispatchConsentEvent('revoke', revokedCategories);

  closePreferencesModal();
  hideRevisitButton();
  showBanner();
}

/**
 * 現在の同意状態を取得する
 * @returns {Object|null}
 */
function getConsent() {
  if (!_config) return null;
  return readConsent(_config.cookie.name);
}

/**
 * 特定カテゴリの同意状態を取得する
 * @param {string} category
 * @returns {boolean}
 */
function hasConsent(category) {
  const consent = getConsent();
  if (!consent || !consent.categories) return false;
  return Boolean(consent.categories[category]);
}

/**
 * カスタムイベントを発火する
 * @param {string} action - "initial" | "update" | "revoke"
 * @param {Object} categories
 */
function dispatchConsentEvent(action, categories) {
  document.dispatchEvent(
    new CustomEvent('cmp:consent', {
      detail: { action, categories }
    })
  );
}

/**
 * data-cmp-action属性のクリックイベントハンドラ（documentレベル委譲）
 */
function handleDataAction(e) {
  const el = e.target.closest('[data-cmp-action]');
  if (!el) return;

  const action = el.getAttribute('data-cmp-action');
  if (action === 'show-preferences') {
    e.preventDefault();
    showPreferences();
  }
}

/**
 * 内部状態を完全リセットする（テスト用）
 * @private
 */
function _destroy() {
  hideBanner();
  closePreferencesModal();
  hideRevisitButton();
  stopObserver();
  resetMemoryFallback();
  if (_initialized) {
    document.removeEventListener('click', handleDataAction);
    _initialized = false;
  }
  _config = null;
  _texts = null;
}

// 公開API
export default {
  run,
  showPreferences,
  acceptAll,
  acceptNecessary,
  savePreferences,
  getConsent,
  hasConsent,
  reset,
  _destroy
};
